// ===== Estructura de datos del modulo Caja / Arqueo =====

export type CashSessionStatus = 'abierta' | 'cerrada';
export type CashMovementType = 'ingreso' | 'egreso';
// Metodos soportados al cobrar. Las pasarelas (Yape/Plin) y tarjetas separadas
// permiten desglose fino en el arqueo del cierre.
export type PaymentMethod =
  | 'efectivo'
  | 'efectivo_usd'
  | 'visa'
  | 'mastercard'
  | 'tarjeta' // legacy generico
  | 'yape'
  | 'plin'
  | 'transferencia'
  | 'credito';

// Movimiento manual de efectivo dentro de un turno.
export interface CashMovement {
  id: number;
  sessionId: number;
  type: CashMovementType;
  amount: number;
  concept: string;
  createdById: number | null;
  createdByName: string | null;
  createdAt: string;
}

// Turno de caja (apertura -> cierre).
export interface CashSession {
  id: number;
  status: CashSessionStatus;
  openedById: number | null;
  openedByName: string | null;
  openingAmount: number;
  openingAmountUsd: number;
  exchangeRate: number;
  openedAt: string;
  closedAt: string | null;
  closedById: number | null;
  closedByName: string | null;
  // Snapshot al cierre:
  countedCash: number | null;
  countedCashUsd: number | null;
  expectedCash: number | null;
  expectedCashUsd: number | null;
  difference: number | null;
  differenceUsd: number | null;
  totalSales: number | null;
  salesEfectivo: number | null;
  salesEfectivoUsd: number | null;
  salesVisa: number | null;
  salesMastercard: number | null;
  salesTarjeta: number | null;
  salesYape: number | null;
  salesPlin: number | null;
  salesTransferencia: number | null;
  salesCredito: number | null;
  totalIngresos: number | null;
  totalEgresos: number | null;
  ordersCount: number | null;
  notes: string | null;
  updatedAt: string;
}

// Desglose de ventas por metodo (resumen en vivo).
export interface CashSalesBreakdown {
  efectivo: number;
  efectivoUsd: number;
  visa: number;
  mastercard: number;
  tarjeta: number; // agregado: visa + mastercard (legacy)
  yape: number;
  plin: number;
  transferencia: number;
  credito: number;
  total: number;
  count: number;
}

// Respuesta de GET /cash/current.
export interface CashCurrent {
  open: boolean;
  session?: CashSession;
  sales?: CashSalesBreakdown;
  movements?: CashMovement[];
  ingresos?: number;
  egresos?: number;
  expectedCash?: number;
  expectedCashUsd?: number;
}

// Gastos de la sesion abierta (egresos de cash_movements).
export interface CashExpensesResponse {
  sessionId: number | null;
  expenses: CashMovement[];
  total: number;
}

// Respuesta de GET /cash/:id (resumen de una sesion abierta o cerrada).
export interface CashSummary {
  session: CashSession;
  sales: CashSalesBreakdown;
  movements: CashMovement[];
  ingresos: number;
  egresos: number;
  expectedCash: number;
  expectedCashUsd: number;
}

// Filtros del historial de cajas (solo admin).
export interface CashHistoryFilters {
  from?: string; // YYYY-MM-DD
  to?: string;   // YYYY-MM-DD
  cajero?: string;
}

// Tipo de cambio SUNAT del dia.
export interface ExchangeRate {
  date: string;
  buy: number;
  sell: number;
  source: string;
  cached: boolean;
}

// Inputs para apertura y cierre.
export interface OpenCashInput {
  openingAmount: number;
  openingAmountUsd?: number;
  exchangeRate?: number;
}

export interface CloseCashInput {
  countedCash: number;
  countedCashUsd?: number;
  notes?: string;
}

// Denominaciones de Soles para la calculadora de arqueo.
export const SOLES_DENOMINATIONS: { value: number; label: string; kind: 'billete' | 'moneda' }[] = [
  { value: 200, label: 'S/ 200', kind: 'billete' },
  { value: 100, label: 'S/ 100', kind: 'billete' },
  { value: 50, label: 'S/ 50', kind: 'billete' },
  { value: 20, label: 'S/ 20', kind: 'billete' },
  { value: 10, label: 'S/ 10', kind: 'billete' },
  { value: 5, label: 'S/ 5', kind: 'moneda' },
  { value: 2, label: 'S/ 2', kind: 'moneda' },
  { value: 1, label: 'S/ 1', kind: 'moneda' },
  { value: 0.5, label: 'S/ 0.50', kind: 'moneda' },
  { value: 0.2, label: 'S/ 0.20', kind: 'moneda' },
  { value: 0.1, label: 'S/ 0.10', kind: 'moneda' },
];

// Denominaciones de Dolares para la calculadora de arqueo USD.
export const USD_DENOMINATIONS: { value: number; label: string; kind: 'billete' | 'moneda' }[] = [
  { value: 100, label: 'US$ 100', kind: 'billete' },
  { value: 50, label: 'US$ 50', kind: 'billete' },
  { value: 20, label: 'US$ 20', kind: 'billete' },
  { value: 10, label: 'US$ 10', kind: 'billete' },
  { value: 5, label: 'US$ 5', kind: 'billete' },
  { value: 1, label: 'US$ 1', kind: 'billete' },
];
